import React, { useState, useRef } from 'react';
import { api, uploadMedia, deleteUploadedMedia, resolveMediaUrl } from '../../lib/api';
import { useApp } from '../../context/AppContext';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Input } from '../ui/input';
import { ImagePlus, Dumbbell, X, Video } from 'lucide-react';
import { toast } from 'sonner';

export default function PostComposer({ onPosted, bare = false }) {
  const { user, t } = useApp();
  const [text, setText] = useState('');
  const [images, setImages] = useState([]);
  const [videoFile, setVideoFile] = useState(null);
  const [videoPreview, setVideoPreview] = useState('');
  const [showLog, setShowLog] = useState(false);
  const [log, setLog] = useState({ exercise: '', sets: '', reps: '', weight: '' });
  const [busy, setBusy] = useState(false);
  const fileRef = useRef();
  const videoRef = useRef();

  const addImages = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 6 - images.length);
    const selected = files
      .filter((file) => {
        if (file.size <= 8 * 1024 * 1024) return true;
        toast.error(t('imageTooLarge'));
        return false;
      })
      .map((file) => ({ file, preview: URL.createObjectURL(file) }));
    setImages((current) => [...current, ...selected]);
    e.target.value = '';
  };

  const removeImage = (index) => {
    setImages((current) => current.filter((image, currentIndex) => {
      if (currentIndex === index) URL.revokeObjectURL(image.preview);
      return currentIndex !== index;
    }));
  };

  const clearImages = () => {
    images.forEach((image) => URL.revokeObjectURL(image.preview));
    setImages([]);
  };

  const addVideo = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.type.startsWith('video/')) {
      toast.error(t('selectVideo'));
      return;
    }
    if (file.size > 100 * 1024 * 1024) {
      toast.error(t('videoTooLarge'));
      return;
    }
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(file);
    setVideoPreview(URL.createObjectURL(file));
    e.target.value = '';
  };

  const clearVideo = () => {
    if (videoPreview) URL.revokeObjectURL(videoPreview);
    setVideoFile(null);
    setVideoPreview('');
  };

  const submit = async () => {
    if (!text.trim() && images.length === 0 && !videoFile) return;
    setBusy(true);
    const uploadedUrls = [];
    try {
      const exercise_log = showLog && log.exercise
        ? { exercise: log.exercise, sets: Number(log.sets) || 0, reps: log.reps, weight: log.weight }
        : null;
      const imageUrls = [];
      for (const image of images) {
        const uploaded = await uploadMedia(image.file);
        imageUrls.push(uploaded.url);
        uploadedUrls.push(uploaded.url);
      }
      const videos = [];
      if (videoFile) {
        const uploaded = await uploadMedia(videoFile);
        videos.push(uploaded.url);
        uploadedUrls.push(uploaded.url);
      }
      const res = await api.post('/posts', { text, images: imageUrls, videos, exercise_log });
      onPosted?.(res.data);
      setText(''); clearImages(); clearVideo(); setLog({ exercise: '', sets: '', reps: '', weight: '' }); setShowLog(false);
      toast.success(t('postPublished'));
    } catch (err) {
      await Promise.allSettled(uploadedUrls.map(deleteUploadedMedia));
      toast.error(err.response?.data?.detail || t('failedToPost'));
    } finally { setBusy(false); }
  };

  return (
    <div className={bare ? '' : 'bg-card rounded-xl border border-border p-4 elev-1'}>
      <div className="flex gap-3">
        <Avatar className="w-10 h-10">
          <AvatarImage src={resolveMediaUrl(user?.avatar)} />
          <AvatarFallback>{user?.username?.[0]?.toUpperCase()}</AvatarFallback>
        </Avatar>
        <div className="flex-1">
          <Textarea
            data-testid="feed-post-composer-textarea"
            value={text}
            onChange={(e) => setText(e.target.value)}
            placeholder={t('whatsOnYourMind')}
            className="min-h-[60px] resize-none border-0 bg-transparent focus-visible:ring-0 p-0 text-sm"
          />
          {images.length > 0 && (
            <div className="flex gap-2 mt-2 flex-wrap">
              {images.map((image, i) => (
                <div key={i} className="relative">
                  <img src={image.preview} alt="upload" className="w-16 h-16 object-cover rounded-lg border border-border" />
                  <button onClick={() => removeImage(i)}
                    className="absolute -top-1.5 -end-1.5 w-5 h-5 rounded-full bg-destructive flex items-center justify-center">
                    <X size={11} />
                  </button>
                </div>
              ))}
            </div>
          )}
          {videoPreview && (
            <div className="relative mt-3 overflow-hidden rounded-xl border border-border bg-black">
              <video src={resolveMediaUrl(videoPreview)} className="max-h-64 w-full object-contain" controls muted playsInline />
              <button data-testid="feed-post-composer-remove-video" onClick={clearVideo}
                className="absolute top-2 end-2 w-7 h-7 rounded-full bg-black/70 text-white flex items-center justify-center">
                <X size={14} />
              </button>
            </div>
          )}
          {showLog && (
            <div className="grid grid-cols-4 gap-2 mt-3 p-3 rounded-lg bg-secondary/50 border border-primary/20">
              <Input data-testid="composer-log-exercise" placeholder="Exercise" value={log.exercise} onChange={(e) => setLog({ ...log, exercise: e.target.value })} className="col-span-4 sm:col-span-1 h-8 text-xs" />
              <Input placeholder="Sets" value={log.sets} onChange={(e) => setLog({ ...log, sets: e.target.value })} className="h-8 text-xs" />
              <Input placeholder="Reps" value={log.reps} onChange={(e) => setLog({ ...log, reps: e.target.value })} className="h-8 text-xs" />
              <Input placeholder="Weight" value={log.weight} onChange={(e) => setLog({ ...log, weight: e.target.value })} className="h-8 text-xs" />
            </div>
          )}
          <div className="flex items-center justify-between mt-3">
            <div className="flex gap-1">
              <input ref={fileRef} type="file" accept="image/*" multiple hidden onChange={addImages} />
              <input ref={videoRef} type="file" accept="video/*" hidden onChange={addVideo} />
              <button data-testid="feed-post-composer-add-image-button" onClick={() => fileRef.current?.click()}
                className="p-2 rounded-lg hover:bg-secondary text-muted-foreground hover:text-primary transition-colors">
                <ImagePlus size={18} />
              </button>
              <button data-testid="feed-post-composer-add-video-button" onClick={() => videoRef.current?.click()}
                className={`p-2 rounded-lg hover:bg-secondary transition-colors ${videoFile ? 'text-primary bg-secondary' : 'text-muted-foreground hover:text-primary'}`}>
                <Video size={18} />
              </button>
              <button data-testid="feed-post-composer-add-workout-button" onClick={() => setShowLog(!showLog)}
                className={`p-2 rounded-lg hover:bg-secondary transition-colors ${showLog ? 'text-primary bg-secondary' : 'text-muted-foreground'}`}>
                <Dumbbell size={18} />
              </button>
            </div>
            <Button data-testid="feed-post-composer-submit-button" size="sm" onClick={submit} disabled={busy || (!text.trim() && images.length === 0 && !videoFile)} className="font-semibold px-5">
              {busy ? '...' : t('post')}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
